import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const [events, setEvents] = useState([]);

  // Helper function to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api/images/') 
      ? `${process.env.REACT_APP_BACKEND_URL}${url}/data` 
      : url;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    document.title = 'Club JCI Ltd - Events';
  }, []);

  const eventTypes = [
    { title: 'Expert Talks', desc: 'Insights from industry leaders and thought influencers', icon: '🎤' },
    { title: 'Founder Meetups', desc: 'Connect with entrepreneurs from diverse domains', icon: '🤝' },
    { title: 'Cultural Evenings', desc: 'Music, art, and performances celebrating creativity', icon: '🎭' },
    { title: 'Family Days', desc: 'Excursions and activities for members and families', icon: '👨‍👩‍👧‍👦' },
  ];

  return (
    <div data-testid="events-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="events-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Events</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#F1F5F9] mb-6">
              Curated <span className="gold-text">Experiences</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              From expert talks to cultural evenings, our events are designed to inspire, 
              connect, and create lasting memories.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Event Types */}
      <section className="section-padding bg-[#0A192F]" data-testid="event-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-6 rounded-sm text-center"
              >
                <div className="text-4xl mb-4">{type.icon}</div>
                <h3 className="text-lg font-semibold text-[#F1F5F9] mb-2">{type.title}</h3>
                <p className="text-sm text-[#94A3B8]">{type.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="section-padding" data-testid="events-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-4">
              Upcoming <span className="gold-text">Events</span>
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Mark your calendar for these exclusive gatherings.
            </p>
          </motion.div>

          {events.length > 0 ? (
            <div className="space-y-6">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="event-card glass-card rounded-sm overflow-hidden"
                  data-testid={`event-${event.id}`}
                >
                  {event.image_url && (
                    <div className="md:w-1/3 h-48 md:h-auto">
                      <img
                        src={getImageUrl(event.image_url)}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className={`p-6 flex-1 ${event.image_url ? 'md:w-2/3' : ''}`}>
                    {event.is_featured && (
                      <span className="inline-block bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-3 py-1 rounded-sm mb-3">
                        Featured
                      </span>
                    )}
                    <h3 className="text-xl font-semibold text-[#F1F5F9] mb-3">{event.title}</h3>
                    <p className="text-[#94A3B8] mb-4">{event.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                      <span className="flex items-center gap-2">
                        <Calendar size={14} className="text-[#D4AF37]" />
                        {event.date}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock size={14} className="text-[#D4AF37]" />
                        {event.time}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin size={14} className="text-[#D4AF37]" />
                        {event.venue}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-6">📅</div>
              <h3 className="text-xl font-semibold text-[#F1F5F9] mb-2">No Upcoming Events</h3>
              <p className="text-[#94A3B8]">
                Stay tuned for our next curated gathering. New events are announced regularly.
              </p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
